import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Building2, MapPin, Calendar, ChevronRight, AlertTriangle } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import TopHeader from "@/components/TopHeader";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { formatDate, formatMinutesAsHours } from "@/lib/format";

export default function Projekt() {
    const [projects, setProjects] = useState([]);
    const [totals, setTotals] = useState({});

    const load = useCallback(async () => {
        try {
            const [p, t] = await Promise.all([
                api.get("/projects"),
                api.get("/projects/totals"),
            ]);
            setProjects(p.data || []);
            setTotals(t.data || {});
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    }, []);
    useEffect(() => { load(); }, [load]);

    return (
        <div>
            <TopHeader title="Projekte" />
            <main className="mx-auto max-w-2xl px-4 pt-4 safe-bottom" data-testid="page-projekt">
                <div className="mb-3 text-[11px] font-medium uppercase tracking-widest text-mes-muted">
                    {projects.length} Projekte
                </div>
                <div className="space-y-3">
                    {projects.map((p) => {
                        const total = totals[p.id]?.total_minutes || 0;
                        const allowanceMin = (p.stundenkontingent || 0) * 60;
                        const pct = allowanceMin ? Math.min(999, (total / allowanceMin) * 100) : 0;
                        const level = pct >= 100 ? "kritisch" : pct >= 90 ? "hoch" : pct >= 80 ? "achtung" : "ok";
                        const color = level === "kritisch" ? "bg-mes-danger"
                            : level === "hoch" || level === "achtung" ? "bg-mes-warn" : "bg-mes-brand";
                        return (
                            <Link key={p.id} to={`/projekt/${p.id}`} className="block" data-testid={`project-card-${p.id}`}>
                                <Card className="mes-card p-4 transition-shadow hover:shadow-elev">
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center gap-2 text-[11px] font-medium text-mes-muted">
                                                <Building2 className="h-3.5 w-3.5" />
                                                <span>{p.customer}</span>
                                                <StatusPill status={p.status} />
                                            </div>
                                            <div className="mt-0.5 truncate font-display text-[15px] font-semibold text-mes-ink">{p.name}</div>
                                            <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-mes-muted">
                                                <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{p.address}</span>
                                                <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" />{formatDate(p.start_date + "T00:00:00")}</span>
                                            </div>
                                        </div>
                                        <ChevronRight className="h-5 w-5 shrink-0 text-mes-muted" />
                                    </div>

                                    <div className="mt-3 flex items-center justify-between text-xs">
                                        <div>
                                            <span className="text-mes-muted">Kontingent</span>{" "}
                                            <span className="tabular font-semibold text-mes-ink">{formatMinutesAsHours(allowanceMin)} h</span>
                                        </div>
                                        <div>
                                            <span className="text-mes-muted">Geleistet</span>{" "}
                                            <span className="tabular font-semibold text-mes-ink">{formatMinutesAsHours(total)} h</span>
                                        </div>
                                        {level !== "ok" && (
                                            <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                                                level === "kritisch" ? "bg-mes-danger/10 text-mes-danger" : "bg-mes-warnSoft text-mes-warn"
                                            }`}>
                                                <AlertTriangle className="h-3 w-3" /> {pct.toFixed(0)}%
                                            </span>
                                        )}
                                    </div>
                                    <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-mes-surfaceAlt ring-1 ring-mes-border">
                                        <div className={`h-full ${color}`} style={{ width: `${Math.min(100, pct)}%` }} />
                                    </div>
                                </Card>
                            </Link>
                        );
                    })}
                    {projects.length === 0 && (
                        <Card className="mes-card p-6 text-center text-sm text-mes-muted">Keine Projekte zugewiesen.</Card>
                    )}
                </div>
            </main>
        </div>
    );
}

function StatusPill({ status }) {
    const map = {
        aktiv: "bg-mes-brandSoft text-mes-brand",
        pausiert: "bg-mes-warnSoft text-mes-warn",
        abgeschlossen: "bg-mes-surfaceAlt text-mes-muted border border-mes-border",
    };
    return (
        <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${map[status] || "bg-mes-surfaceAlt text-mes-muted"}`}>
            {status}
        </span>
    );
}
